import { FC, useState } from 'react';
import { Box, Button, IconButton, Popover, TextField, Tooltip, Typography } from '@mui/material';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import CloseIcon from '@mui/icons-material/Close';
import { useSearchFilterNotesStore } from '@/stores/searchFilterNotesStore';

const DateRangeFilter: FC = () => {
  const { dateRange, setDateRange } = useSearchFilterNotesStore();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [localStart, setLocalStart] = useState(dateRange.start || '');
  const [localEnd, setLocalEnd] = useState(dateRange.end || '');

  const isOpen = Boolean(anchorEl);
  const hasActiveRange = dateRange.start || dateRange.end;

  const handleToggle = (event: React.MouseEvent<HTMLElement>) => {
    if (!anchorEl) {
      setLocalStart(dateRange.start || '');
      setLocalEnd(dateRange.end || '');
      setAnchorEl(event.currentTarget);
    } else {
      setAnchorEl(null);
    }
  };

  const handleClose = () => setAnchorEl(null);

  const handleApply = () => {
    setDateRange({
      start: localStart || null,
      end: localEnd || null,
    });
    setAnchorEl(null);
  };

  const handleClear = (event?: React.MouseEvent) => {
    event?.stopPropagation();
    setDateRange({ start: null, end: null });
    setLocalStart('');
    setLocalEnd('');
    setAnchorEl(null);
  };

  const formatDisplayDate = (dateStr: string) => {
    if (!dateStr) return '';
    const date = new Date(dateStr + 'T00:00:00');
    return new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric' }).format(date);
  };

  const getChipLabel = () => {
    if (dateRange.start && dateRange.end) {
      return `${formatDisplayDate(dateRange.start)} - ${formatDisplayDate(dateRange.end)}`;
    }
    if (dateRange.start) {
      return `From ${formatDisplayDate(dateRange.start)}`;
    }
    if (dateRange.end) {
      return `Until ${formatDisplayDate(dateRange.end)}`;
    }
    return 'Date range';
  };

  return (
    <Box display="flex" alignItems="center">
      <Tooltip title={hasActiveRange ? getChipLabel() : 'Date range'}>
        <IconButton
          onClick={handleToggle}
          size="small"
          aria-label="Date range filter"
          sx={{
            width: 36,
            height: 36,
            border: '1px solid',
            borderColor: hasActiveRange ? 'primary.main' : 'divider',
            borderRadius: 1,
            backgroundColor: hasActiveRange ? 'rgba(43, 159, 255, 0.08)' : 'transparent',
            color: hasActiveRange ? 'primary.main' : 'action.active',
            '&:hover': {
              backgroundColor: hasActiveRange ? 'rgba(43, 159, 255, 0.12)' : 'action.hover',
            },
          }}
        >
          <CalendarMonthOutlinedIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      {hasActiveRange && (
        <IconButton size="small" onClick={handleClear} sx={{ ml: 0.25, padding: '2px' }}>
          <CloseIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
        </IconButton>
      )}

      <Popover
        open={isOpen}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        <Box sx={{ p: 2, width: 280 }}>
          <Typography variant="caption" fontWeight={500} sx={{ mb: 1, display: 'block' }}>
            Filter by date range
          </Typography>
          <TextField
            type="date"
            label="Start date"
            value={localStart}
            onChange={(e) => setLocalStart(e.target.value)}
            size="small"
            fullWidth
            sx={{ mb: 1.5 }}
            slotProps={{
              inputLabel: { shrink: true },
            }}
          />
          <TextField
            type="date"
            label="End date"
            value={localEnd}
            onChange={(e) => setLocalEnd(e.target.value)}
            size="small"
            fullWidth
            sx={{ mb: 1.5 }}
            slotProps={{
              inputLabel: { shrink: true },
            }}
          />
          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
            <Button size="small" onClick={handleClear} color="inherit">
              Clear
            </Button>
            <Button size="small" variant="contained" onClick={handleApply} disabled={!localStart && !localEnd}>
              Apply
            </Button>
          </Box>
        </Box>
      </Popover>
    </Box>
  );
};

export default DateRangeFilter;
